import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Empty } from "@/components/Kit";
import { useYearOptions } from "@/lib/academicYear";
import { Printer } from "lucide-react";

const fmt = (n) => (Number.isInteger(n) ? n : n.toFixed(1));

export default function MonthlyAttendance({ classes, cls, setCls }) {
  const { years: YEARS, current } = useYearOptions();
  const [year, setYear] = useState("");
  const yr = year || current;
  const [class_name, section] = (cls || "-").split("-");
  const { data } = useQuery({
    queryKey: ["att-monthly", cls, yr], enabled: !!cls && !!yr,
    queryFn: async () => (await api.get("/school/academics/attendance/monthly", { params: { class_name, section, academic_year: yr } })).data,
  });

  return (
    <div data-testid="monthly-attendance">
      <div className="ep-card mb-4 flex flex-wrap items-end gap-4 p-4 no-print">
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Class</span><select className="ep-input" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="am-class">{classes.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Academic Year</span><select className="ep-input" value={yr} onChange={(e) => setYear(e.target.value)} data-testid="am-year">{YEARS.map((y) => <option key={y}>{y}</option>)}</select></label>
        <div className="ml-auto text-right"><p className="text-xs text-slate-400">Total working days</p><p className="text-lg font-bold font-head text-white" data-testid="am-total-working">{data?.total_working ?? 0}</p></div>
        <button className="ep-btn ep-btn-ghost" onClick={() => window.print()} data-testid="am-print-btn"><Printer size={15} /> Print</button>
      </div>
      {!data ? <div className="ep-card"><Empty>Select a class</Empty></div> : (
        <div className="ep-card overflow-x-auto print-area">
          <table className="w-full text-xs" data-testid="am-table">
            <thead>
              <tr className="border-b border-slate-800 text-left uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Student</th>
                {data.months.map((m) => <th key={m.key} className="px-2 py-3 text-center">{m.label}</th>)}
                <th className="px-3 py-3 text-center">Total</th><th className="px-3 py-3 text-center">%</th>
              </tr>
              <tr className="border-b border-slate-800 bg-slate-800/40 text-slate-300" data-testid="am-working-row">
                <td className="px-3 py-2 font-semibold">Working Days</td>
                {data.months.map((m) => <td key={m.key} className="px-2 py-2 text-center font-mono">{data.working_days[m.key]}</td>)}
                <td className="px-3 py-2 text-center font-mono font-semibold">{data.total_working}</td><td />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`am-row-${r.admission_no}`}>
                  <td className="px-3 py-2"><p className="font-medium text-white">{r.name}</p><p className="font-mono text-[10px] text-slate-500">{r.admission_no}</p></td>
                  {data.months.map((m) => <td key={m.key} className="px-2 py-2 text-center font-mono text-slate-300">{data.working_days[m.key] ? fmt(r.attended[m.key]) : "—"}</td>)}
                  <td className="px-3 py-2 text-center font-mono font-semibold text-white">{fmt(r.total_attended)}</td>
                  <td className={`px-3 py-2 text-center font-semibold ${r.percentage >= 75 ? "text-emerald-400" : "text-amber-400"}`}>{r.percentage}%</td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={data.months.length + 3}><Empty>No students in this class</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
